import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Declarative `canvas` Animation with React and Konva",
  "description": "",
  "published": "2017-02-16T00:00:00.000Z",
  "redirect_from": ["/blog/declarative-canvas-animation-react-konva/swizec/7443"],
  "categories": "Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <lite-youtube {...{
      "videoid": "oli1qacE-wo?ecver=2",
      "videostartat": "0"
    }}></lite-youtube>
    <p>{`Today we're looking at declarative animation that renders on `}<inlineCode parentName="p">{`<canvas>`}</inlineCode>{`: React for declarativeness, `}<a parentName="p" {...{
        "href": "https://konvajs.github.io/"
      }}>{`Konva`}</a>{` as a canvas abstraction layer, and `}<a parentName="p" {...{
        "href": "https://github.com/lavrton/react-konva"
      }}>{`react-konva`}</a>{` to make them work together.`}</p>
    <p>{`In theory, this combination has better performance than SVG+React but worse performance than raw canvas because of the additional abstraction layers. We have canvas as the rendering layer, then Konva gives us basic shapes and interactions, `}<inlineCode parentName="p">{`react-konva`}</inlineCode>{` turns those into React components, and our own React code makes it work together.`}</p>
    <p>{`If that sounds complicated… it probably is. I barely know how to use it, and I have no idea how it `}<em parentName="p">{`actually`}</em>{` works.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2017/02/bouncing-marbles-1.gif",
        "alt": null
      }}></img></p>
    <p>{`We're building a marble simulation. You can pick up a marble and throw it, and it bounces around until it stops. I wanted to add collision detection as well, but `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/N-body_problem"
      }}>{`N-body collisions are hard`}</a>{`. Next time!`}</p>
    <p>{`You can see the `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/declarative-canvas-react-konva"
      }}>{`code on Github`}</a>{` and play with marbles on the `}<a parentName="p" {...{
        "href": "https://swizec.github.io/declarative-canvas-react-konva/"
      }}>{`live demo`}</a>{`. The live demo looks better than the gif, I promise.`}</p>
    <p>{`We have two components:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`Marble`}</inlineCode>{`, which renders each marble and deals with drag events`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`Collisions`}</inlineCode>{`, which renders all the marbles and deals with the game loop logic`}</li>
    </ul>
    <p>{`Yes, the game loop logic should totally be a Redux or MobX store instead of shoved into a component. This is fine. Small example ?`}</p>
    <h2 {...{
      "id": "marble"
    }}>{`Marble`}</h2>
    <p>{`The `}<inlineCode parentName="p">{`Marble`}</inlineCode>{` component uses `}<inlineCode parentName="p">{`react-konva`}</inlineCode>{` to render a `}<inlineCode parentName="p">{`<Circle>`}</inlineCode>{` and listen for a `}<inlineCode parentName="p">{`dragend`}</inlineCode>{` event. That's how you "throw".`}</p>
    <p>{`You can think of react-konva as a very thin abstraction layer on Konva. I looked at the source code once, and it just uses a bit of magic to translate all of `}<a parentName="p" {...{
        "href": "https://konvajs.github.io/api/Konva.html"
      }}>{`Konva's classes`}</a>{` into React components. Props are passed through unchanged as Konva attributes.`}</p>
    <p>{`That means you don't have to think about using react-konva. Focus on the Konva docs, it's all the same.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`class Marble extends Component {
    onDragEnd() {
        const { x, y } = this.props,
              circle = this.refs.circle;

        this.props.onShoot({
            x: circle.attrs.x,
            y: circle.attrs.y,
            vx: (circle.attrs.x-x)/7,
            vy: (circle.attrs.y-y)/7
        });
    }

    render() {
        const { x, y, sprite, type, draggable } = this.props;

        return (
            <circle 111="" x={x} y={y} radius={MarbleR} fillpatternimage={sprite} fillpatternoffset={Marbles[type]} fillpatternscale={{ x: marbler*2 111, y: }} shadowcolor={Marbles[type].c} shadowblur="15" shadowopacity="1" draggable={draggable} ondragend={this.onDragEnd.bind(this)} ref="circle">
        );
    }
}
</circle>
`}</code></pre>
    <p>{`We're rendering a `}<inlineCode parentName="p">{`<Circle>`}</inlineCode>{` element at position `}<inlineCode parentName="p">{`(x, y)`}</inlineCode>{` and giving it a radius of `}<inlineCode parentName="p">{`15`}</inlineCode>{`. Very similar to SVG, right?`}</p>
    <p>{`Here's where it gets crazy. To get the marble look, we use `}<inlineCode parentName="p">{`fillPattern*`}</inlineCode>{` props and use a `}<inlineCode parentName="p">{`sprite`}</inlineCode>{` for the background. We reposition and scale it to get the sprite to fit and make marbles look different.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2017/02/sprite.png",
        "alt": null
      }}></img></p>
    <p>{`For the shine effect, we use `}<inlineCode parentName="p">{`shadow*`}</inlineCode>{` props. Shadows get a color that matches each marble (I used Photoshop), some blur, and an opacity. This gives each marble a glow that makes the marbles look shiny.`}</p>
    <p>{`They're still shadows though, so a bunch of things are wrong. Especially when the marbles get close together, you can see that the shadows look darker when combined. Real shines would look brighter.`}</p>
    <p>{`To get draggability, we turn it on. Konva handles the rest for us. `}<inlineCode parentName="p">{`onDragEnd`}</inlineCode>{` we call the `}<inlineCode parentName="p">{`onShoot`}</inlineCode>{` callback with the marble's new position and movement vector. This part is janky. I'll explain why later.`}</p>
    <h2 {...{
      "id": "collisions"
    }}>{`Collisions`}</h2>
    <p>{`The `}<inlineCode parentName="p">{`<Collisions>`}</inlineCode>{` component is called collisions because this was meant to be a simulation of `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Inelastic_collision"
      }}>{`inelastic N-body collisions`}</a>{`. High school physics stuff.`}</p>
    <p>{`But that's hard to do, so you get just the bouncing off of walls.`}</p>
    <p>{`This component has three major parts:`}</p>
    <ul>
      <li parentName="ul">{`calculating the initial positions to make a triangle`}</li>
      <li parentName="ul">{`the game loop that drives animation`}</li>
      <li parentName="ul">{`declaratively rendering the marbles`}</li>
    </ul>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`class Collisions extends Component {
    constructor(props) {
        // setting up this.state
    }

    get initialPositions() {
        // calculating initial positions
    }

    componentDidMount() {
        // loading sprite
    }

    shoot(newPos, i) {
        // updating a thrown marble
    }

    gameLoop() {
        // moving
    }

    render() {
            // rendering
    }
`}</code></pre>
    <h3 {...{
      "id": "initial-positions-and-sprite-loading"
    }}>{`Initial positions and sprite loading`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`    componentDidMount() {
        const sprite = new Image();
        sprite.src = MarbleSprite;

        sprite.onload = () => {
            this.setState({
                sprite: sprite
            });

            this.timer = timer(() => this.gameLoop());
        };
    }
`}</code></pre>
    <p>{`Konva takes sprites as ES6 `}<inlineCode parentName="p">{`Image`}</inlineCode>{` objects. We load one up, wait for the `}<inlineCode parentName="p">{`onload`}</inlineCode>{` event to fire, add it to state, which triggers a re-render, and start the game loop timer.`}</p>
    <p>{`If you're not familiar with the `}<inlineCode parentName="p">{`Image`}</inlineCode>{` object, it's basically an in-memory representation of the image bytestream. Unless you're doing something very particular, you don't need to know the details. It loads an image into memory :)`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`    get initialPositions() {
        const { width, height } = this.props,
              center = width/2;

        let marbles = range(3, 0, -1).map(y => {
            if (y === 3) return [{ x: center, y: 200,
                                   vx: 0, vy: 0}];

            const left = center - y*(MarbleR+5),
                  right = center + y*(MarbleR+5);

            return range(left, right, MarbleR*2+5).map(x => ({
                x: x,
                y: 200-y*(MarbleR*2+5),
                vx: 0,
                vy: 0
            }));
        }).reduce((acc, pos) => acc.concat(pos), []);

        marbles = marbles.concat({
            x: width/2,
            y: height-150,
            vx: 0,
            vy: 0
        });

        return marbles;
    }
`}</code></pre>
    <p>{`This… this took me embarrassingly long to code. It's one of those interview question things: Render stuff in a triangle. Then you fumble for an hour, and they're like `}<em parentName="p">{`"LoL you're an idiot, pass"`}</em>{`.`}</p>
    <p>{`That was going through my mind the entire time. How the hell am I struggling `}<em parentName="p">{`this`}</em>{` hard to put marbles in a triangle?`}</p>
    <p>{`Here's how it works:`}</p>
    <ul>
      <li parentName="ul">{`loop from 3 to 0 to create the rows`}</li>
      <li parentName="ul">{`in each row. go from the `}<inlineCode parentName="li">{`left`}</inlineCode>{` edge to the `}<inlineCode parentName="li">{`right`}</inlineCode>{` edge with a step of "marble size"`}</li>
      <li parentName="ul">{`add position to array`}</li>
    </ul>
    <p>{`You get the left edge is `}<inlineCode parentName="p">{`y`}</inlineCode>{` marble halves to the left, and the right is `}<inlineCode parentName="p">{`y`}</inlineCode>{` marble halves to the right. This nested loop approach returns a nested array so you flatten it with a `}<inlineCode parentName="p">{`.reduce`}</inlineCode>{`.`}</p>
    <p>{`Oh, and those `}<inlineCode parentName="p">{`range()`}</inlineCode>{` functions are actually `}<inlineCode parentName="p">{`d3.range`}</inlineCode>{`. I got them with `}<inlineCode parentName="p">{`import { range } from 'd3-array'`}</inlineCode>{`.`}</p>
    <h3 {...{
      "id": "game-loop"
    }}>{`Game loop`}</h3>
    <p>{`Our game loop is a function that `}<inlineCode parentName="p">{`d3.timer`}</inlineCode>{` calls on every `}<inlineCode parentName="p">{`requestAnimationFrame`}</inlineCode>{`. It goes through our array of marbles, updates their positions, and triggers a re-render.`}</p>
    <p>{`Like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`    shoot(newPos, i) {
        let marbles = this.state.marbles;

        marbles[i] = newPos;

        this.setState({
            marbles: marbles
        });
    }

    gameLoop() {
        const { width, height } = this.props;

        const moveMarble = ({x, y, vx, vy}) => ({
            x: x+vx,
            y: y+vy,
            vx: ((x+vx < MarbleR) ? -vx : (x+vx > width-MarbleR) ? -vx : vx)*.99,
            vy: ((y+vy < MarbleR) ? -vy : (y+vy > height-MarbleR) ? -vy : vy)*.99
        });

        this.setState({
            marbles: this.state.marbles.map(moveMarble)
        });
    }
`}</code></pre>
    <p>{`See? Loop through marbles and update their positions by adding the speed vector to the position. We invert the speed vector when a marble is about to hit a wall in the next step.`}</p>
    <p>{`Nested ternary expressions are hard to read. I should refactor that. If `}<inlineCode parentName="p">{`x+vx`}</inlineCode>{` is smaller than left edge, invert `}<inlineCode parentName="p">{`vx`}</inlineCode>{`. Otherwise if `}<inlineCode parentName="p">{`x+vx`}</inlineCode>{` is bigger than right edge, invert `}<inlineCode parentName="p">{`vx`}</inlineCode>{`. Otherwise, leave it alone.`}</p>
    <p>{`The `}<inlineCode parentName="p">{`shoot()`}</inlineCode>{` function is that dragend callback that `}<inlineCode parentName="p">{`<Marble>`}</inlineCode>{` calls. It updates the particular marble with the new position and the new speed vector.`}</p>
    <h3 {...{
      "id": "rendering"
    }}>{`Rendering`}</h3>
    <p>{`After all that logic, rendering is the easy part. We loop through the marbles and declaratively add them to the `}<inlineCode parentName="p">{`Stage`}</inlineCode>{`. Stage is what Konva calls the canvas element. I don't fully understand why, but I'm sure there's a reason.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`    render() {
        const { sprite } = this.state,
              { width, height } = this.props,
              marbleTypes = Object.keys(Marbles);

        if (!sprite) {
            return (<h2>Loading sprites ...</h2>);
        }

        return (
            <stage width={width} height={height}>
                <layer>
                    <group>
                        {this.state.marbles.map(({x: x, y: y}, i) => (
                            <marble x={x} y={y} type={marbleTypes[i%marbleTypes.length]} sprite={sprite} draggable="true" onshoot="{(newPos)" ==""> this.shoot(newPos, i)}
                                    key={\`marble-\${i}\`} />
                        ))}
                    </marble></group>
                </layer>
            </stage>
        )
    }
`}</code></pre>
    <p>{`See? Loop through `}<inlineCode parentName="p">{`marbles`}</inlineCode>{`, put down `}<inlineCode parentName="p">{`Marble`}</inlineCode>{` components. All this inside a `}<inlineCode parentName="p">{`Stage`}</inlineCode>{`, which is the canvas, and `}<inlineCode parentName="p">{`Layer`}</inlineCode>{`, which I think makes more sense when you have more than one, and `}<inlineCode parentName="p">{`Group`}</inlineCode>{` which is the same concept as SVG's `}<inlineCode parentName="p">{`<g>`}</inlineCode>{` element. It helps you think of groups of shapes as a single thing.`}</p>
    <p>{`A Stage must always have at least one Layer. So that part is important albeit seemingly useless.`}</p>
    <h2 {...{
      "id": "why-is-your-demo-so-janky-swizec"
    }}>{`Why is your demo so janky, Swizec?`}</h2>
    <p>{`Did you guess it yet? Why am I having so much trouble throwing marbles at `}<a parentName="p" {...{
        "href": "https://youtu.be/oli1qacE-wo?t=14"
      }}>{`0:14 in the video`}</a>{`?`}</p>
    <p>{`It's the game loop and Konva fighting each other. The game loop re-renders all our marbles every 16 milliseconds. Konva isn't telling React that they've moved, so the position is reset.`}</p>
    <p>{`That means you `}<em parentName="p">{`have to`}</em>{` complete your throw within 16ms or it won't work.`}</p>
    <p>{`Now, while this looks really bad, it's not a fundamental limitation of the React-Konva-Canvas stack. Just an extra step to take care of before I tackle the N-body collisions.`}</p>
    <p>{`Gotta add a `}<inlineCode parentName="p">{`dragmove`}</inlineCode>{` listener to `}<inlineCode parentName="p">{`Marble`}</inlineCode>{` and make sure it updates React state. Should be easy ?`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      